import { JWTPayload } from '../types/auth';

/**
 * Decode JWT token payload without verification
 * @param token - JWT token string
 * @returns Decoded payload or null if invalid
 */
export const decodeJWTPayload = (token: string): JWTPayload | null => {
  try {
    // JWT tokens have 3 parts separated by dots: header.payload.signature
    const parts = token.split('.');
    if (parts.length !== 3) {
      console.error('Invalid JWT token format');
      return null;
    }

    // Decode the payload (second part)
    const payload = parts[1];
    
    // Add padding if needed for base64 decoding
    const paddedPayload = payload + '='.repeat((4 - payload.length % 4) % 4);
    
    // Decode base64
    const decodedPayload = atob(paddedPayload);
    
    // Parse JSON
    const parsedPayload = JSON.parse(decodedPayload);
    
    return parsedPayload as JWTPayload;
  } catch (error) {
    console.error('Error decoding JWT token:', error);
    return null;
  }
};

/**
 * Extract session ID from JWT token
 * @param token - JWT token string
 * @returns Session ID or null if not found
 */
export const extractSessionId = (token: string): string | null => {
  const payload = decodeJWTPayload(token);
  return payload?.session_id || null;
};

/**
 * Extract user ID from JWT token
 * @param token - JWT token string
 * @returns User ID or null if not found
 */
export const extractUserId = (token: string): number | null => {
  const payload = decodeJWTPayload(token);
  return payload?.user_id || null;
};

/**
 * Check if token is expired
 * @param token - JWT token string
 * @returns True if token is expired
 */
export const isTokenExpired = (token: string): boolean => {
  const payload = decodeJWTPayload(token);
  if (!payload?.exp) {
    return true;
  }
  
  const now = Math.floor(Date.now() / 1000);
  return now > payload.exp;
};
